"use client";

import React, { useState } from 'react';
import { DataProcessingDisclosure } from '@/components/compliance/ConsentManager';
import { trackFormSubmission } from '@/lib/analytics';

// Interface for the debounce function's shape
// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface DebounceFunction {
  (form: NewsletterFormElement): Promise<void>;
}

// Custom debounce function with TypeScript types
const debounce = (func: (form: NewsletterFormElement) => Promise<void>, wait: number) => {
  let timeout: NodeJS.Timeout;
  return (form: NewsletterFormElement) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(form), wait);
  };
};

// Interface for the message state
interface FormMessage {
  text: string;
  type: 'success' | 'error' | null;
}

// Interface for the form element (optional, for clarity)
interface NewsletterFormElement extends HTMLFormElement {
  email: HTMLInputElement;
  botField: HTMLInputElement;
}

export const NewsletterForm = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<FormMessage>({
    text: '',
    type: null,
  });

  // Async function to handle the actual form submission
  const submitForm = async (form: NewsletterFormElement) => {
    const email = form.email.value;
    const botField = form.botField.value; // Check honeypot field
    
    // Honeypot check: if botField is filled, likely a bot
    if (botField) {
      setMessage({ text: 'Submission blocked. Please try again.', type: 'error' });
      return;
    }

    // Basic email format check
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setMessage({ text: 'Please enter a valid email address.', type: 'error' });
      return;
    }

    setIsLoading(true);
    setMessage({ text: '', type: null }); // Clear previous message

    try {
      // Send botField to API for server-side check
      const response = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, botField }),
      });

      if (response.ok) {
        setMessage({ text: 'Subscribed successfully!', type: 'success' });
        form.reset();
        trackFormSubmission('Newsletter', true);
      } else {
        const errorData = await response.json();
        setMessage({ text: errorData.error || 'Subscription failed. Please try again.', type: 'error' });
        trackFormSubmission('Newsletter', false);
      }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      setMessage({ text: 'An error occurred. Please try again later.', type: 'error' });
      trackFormSubmission('Newsletter', false);
    } finally {
      setIsLoading(false);
    }
  };

  // Debounce the submission (e.g., 500ms delay)
  const debouncedSubmit = debounce((form: NewsletterFormElement) => submitForm(form), 500);

  // Handle form submission event
  const handleSubmit = (e: React.FormEvent<NewsletterFormElement>) => {
    e.preventDefault();
    const form = e.target as NewsletterFormElement;
    debouncedSubmit(form);
  };

  return (
    <div className="max-w-3xl mx-auto text-center">
      <h2 className="mb-4">Stay Updated</h2>
      <p className="text-lg mb-8">
        Subscribe to our newsletter to receive the latest insights and updates on commerce, technology, and customer experience.
      </p>
      <form onSubmit={handleSubmit} className="flex flex-col md:flex-row gap-4 max-w-xl mx-auto">
        <label htmlFor="email" className="sr-only">
          Email Address
        </label>
        <input
          type="email"
          name="email"
          id="email"
          placeholder="Enter your email"
          className="flex-grow px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00A4BD]"
          required
          disabled={isLoading}
        />
        {/* Honeypot field: hidden from users, visible to bots */}
        <input
          type="text"
          name="botField"
          id="botField"
          className="hidden"
          style={{ display: 'none' }}
          tabIndex={-1}
          autoComplete="off"
        />
        <button 
          type="submit"
          className="btn-primary whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isLoading}
          aria-busy={isLoading}
        >
          {isLoading ? 'Subscribing...' : 'Subscribe'}
        </button>
      </form>
      {message.text && (
        <p
          className={`text-sm mt-4 ${
            message.type === 'success' ? 'text-green-600' : 'text-red-600'
          }`}
          role="status"
        >
          {message.text}
        </p>
      )}
      <DataProcessingDisclosure className="text-sm text-gray-500 mt-4" />
    </div>
  );
};